import {
  SCB_TENANT_PAYMENT_STATUS_LABEL,
  ScbTenantPaymentStatus,
} from './scb.enums';
import {
  ScbMyBillingNestedInvoice,
  ScbMyBillingPendingInvoiceDetail,
  ScbMyBillingTenantPaymentFlags,
} from './scb.models';

export function extractScbData<T>(resp: unknown): T | null {
  if (resp == null) return null;
  if (typeof resp === 'object' && 'data' in (resp as object)) {
    return ((resp as { data: T | null }).data ?? null) as T | null;
  }
  return resp as T;
}

export function canPayMyBillingInvoice(row: ScbMyBillingTenantPaymentFlags | null | undefined): boolean {
  if (!row) return false;
  if (row.canPayInvoice != null) return !!row.canPayInvoice;
  return (row.tenantPaymentStatus ?? ScbTenantPaymentStatus.NotPaid) === ScbTenantPaymentStatus.NotPaid;
}

export function canSubmitMyBillingProof(row: ScbMyBillingTenantPaymentFlags | null | undefined): boolean {
  if (!row) return false;
  if (row.canSubmitProof != null) return !!row.canSubmitProof;
  const status = row.tenantPaymentStatus ?? ScbTenantPaymentStatus.NotPaid;
  return (
    status === ScbTenantPaymentStatus.NotPaid ||
    status === ScbTenantPaymentStatus.WaitingForProof
  );
}

export function tenantPaymentStatusLabel(row: ScbMyBillingTenantPaymentFlags | null | undefined): string {
  if (!row) return SCB_TENANT_PAYMENT_STATUS_LABEL[ScbTenantPaymentStatus.NotPaid];
  const named = row.tenantPaymentStatusName?.trim();
  if (named) {
    return named
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .trim();
  }
  const status = row.tenantPaymentStatus ?? ScbTenantPaymentStatus.NotPaid;
  return SCB_TENANT_PAYMENT_STATUS_LABEL[status] ?? SCB_TENANT_PAYMENT_STATUS_LABEL[ScbTenantPaymentStatus.NotPaid];
}

export function tenantPaymentStatusTone(
  row: ScbMyBillingTenantPaymentFlags | null | undefined
): 'muted' | 'warn' | 'info' {
  const status = row?.tenantPaymentStatus ?? ScbTenantPaymentStatus.NotPaid;
  if (status === ScbTenantPaymentStatus.WaitingForProof) return 'warn';
  if (status === ScbTenantPaymentStatus.AwaitingPlatformConfirmation) return 'info';
  return 'muted';
}

/** Resolve nested sales-invoice object from tenant my-billing detail payload. */
export function extractMyBillingInvoicePrintPayload(
  detail: ScbMyBillingPendingInvoiceDetail | null | undefined
): ScbMyBillingNestedInvoice | null {
  if (!detail || typeof detail !== 'object') return null;

  const nested = detail.invoice;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested;
  }

  const first = Array.isArray(detail.invoices) ? detail.invoices[0] : null;
  const fromList = first?.invoice;
  if (fromList && typeof fromList === 'object' && !Array.isArray(fromList)) {
    return fromList;
  }

  // Fallback: API returned the sales invoice at the root of `data`.
  const root = detail as ScbMyBillingNestedInvoice;
  if (root.id || root.invoiceNumber || Array.isArray(root.lines)) {
    return root;
  }

  return null;
}

export function extractScbList<T>(resp: unknown): T[] {
  const data = extractScbData<T[] | { items?: T[] }>(resp);
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { items?: T[] }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

export function extractProductOptions(resp: unknown): { id: string; name: string }[] {
  const raw = extractScbData<unknown>(resp) ?? resp;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { items?: unknown[] })?.items)
      ? (raw as { items: unknown[] }).items
      : [];
  return list
    .map((p) => {
      const row = p as { id?: string; productName?: string; name?: string };
      if (!row?.id) return null;
      return { id: row.id, name: row.productName || row.name || row.id };
    })
    .filter((x): x is { id: string; name: string } => !!x);
}

export function pageSlice<T>(items: T[], pageIndex: number, pageSize: number): T[] {
  const start = pageIndex * pageSize;
  return items.slice(start, start + pageSize);
}

export function filterByKeyword<T extends Record<string, unknown>>(
  items: T[],
  keyword: string,
  keys: (keyof T)[]
): T[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    keys.some((k) => String(item[k] ?? '').toLowerCase().includes(q))
  );
}
